<script lang="ts">
  import { session } from '$lib/session.svelte';
  import Card from '$lib/components/Card.svelte';

  const role = $derived(session.user?.role);
  const isStaff = $derived(role === 'admin' || role === 'user');
  const isAdmin = $derived(role === 'admin');
</script>

<div class="page">
  <h2>Settings</h2>
  <p class="sub">Portal configuration for your role.</p>

  {#if isStaff}
    <div class="grid">
      <a class="tile" href="#/settings/skus">
        <Card title="SKU Manager" subtitle="Product codes + profile assignment">
          <p class="muted">
            Create and edit SKU codes used at device registration. Each SKU has exactly one device
            profile.
          </p>
          <span class="cta">Open SKU Manager →</span>
        </Card>
      </a>
      <a class="tile" href="#/settings/profiles">
        <Card title="Profiles" subtitle="Device profile JSON library">
          <p class="muted">
            Upload, view, replace, and delete full profile JSON files. Assignment to SKUs is done in
            SKU Manager.
          </p>
          <span class="cta">Open Profiles →</span>
        </Card>
      </a>
      <a class="tile" href="#/settings/tenants">
        <Card title="Tenants" subtitle="Client organizations + user limits">
          <p class="muted">
            Create new client organizations and set how many client-user accounts each one may have.
          </p>
          <span class="cta">Open Tenants →</span>
        </Card>
      </a>
      <a class="tile" href="#/settings/support">
        <Card title="Support Access" subtitle="Act on a client's behalf, with their permission">
          <p class="muted">
            Organizations that have allowed staff support access right now. Enter one to act exactly as
            that client until you exit.
          </p>
          <span class="cta">Open Support Access →</span>
        </Card>
      </a>
      <a class="tile" href="#/settings/ingress-connections">
        <Card title="Ingress Connection Methods" subtitle="MQTT/MQTTS broker settings + HTTPS URLs">
          <p class="muted">
            Staff-only. Pre-create the connections Device Pools use to receive device payloads.
            Manage pools themselves from the Device Pool section in the main sidebar.
          </p>
          <span class="cta">Open Ingress Connection Methods →</span>
        </Card>
      </a>
      <a class="tile" href="#/settings/command-sets">
        <Card title="Command Sets" subtitle="OTA command.json library, one per SKU">
          <p class="muted">
            Firmware-authored OTA command definitions. Upload/replace here; attach a set to a SKU in
            SKU Manager.
          </p>
          <span class="cta">Open Command Sets →</span>
        </Card>
      </a>
      {#if isAdmin}
        <a class="tile" href="#/staff">
          <Card title="Staff users" subtitle="Admin/user accounts on this platform">
            <p class="muted">Create and manage staff accounts — admin and user roles.</p>
            <span class="cta">Open Staff users →</span>
          </Card>
        </a>
      {/if}
      <a class="tile" href="#/account">
        <Card title="Account" subtitle="Your own login + password">
          <p class="muted">Change your password and view your own account details.</p>
          <span class="cta">Open Account →</span>
        </Card>
      </a>
    </div>
  {:else}
    <div class="grid">
      <a class="tile" href="#/settings/organization">
        <Card title="Organization" subtitle="Your org name + team accounts">
          <p class="muted">
            Rename your organization and manage your team's client-user accounts — create, disable, or
            reset a password.
          </p>
          <span class="cta">Open Organization →</span>
        </Card>
      </a>
      <a class="tile" href="#/settings/formats">
        <Card title="Upstream Formats" subtitle="Sampling + alarm key mapping">
          <p class="muted">
            Map your device profile's parameters to the key names, units, and alarm labels your
            platform expects. Attach a format to a group from the group's page.
          </p>
          <span class="cta">Open Upstream Formats →</span>
        </Card>
      </a>
      <a class="tile" href="#/settings/connections">
        <Card title="Connections" subtitle="HTTPS + MQTT delivery targets">
          <p class="muted">
            Where UCC delivers your groups' upstream data. Up to 5 HTTPS endpoints and 5 MQTT clients.
          </p>
          <span class="cta">Open Connections →</span>
        </Card>
      </a>
      <a class="tile" href="#/settings/support">
        <Card title="Support Access" subtitle="Let Unarvu staff help debug your setup">
          <p class="muted">
            Allow staff to act on your behalf temporarily — no password sharing, fully logged, revoke
            any time.
          </p>
          <span class="cta">Open Support Access →</span>
        </Card>
      </a>
      <a class="tile" href="#/account">
        <Card title="Account" subtitle="Your own login + password">
          <p class="muted">Change your password and view your own account details.</p>
          <span class="cta">Open Account →</span>
        </Card>
      </a>
    </div>
  {/if}
</div>

<style>
  .page {
    max-width: 48rem;
  }
  h2 {
    margin: 0;
    font-size: 1.5rem;
  }
  .sub {
    margin: 0.35rem 0 1.25rem;
    color: var(--text-muted);
  }
  .grid {
    display: grid;
    gap: 1rem;
  }
  @media (min-width: 720px) {
    .grid {
      grid-template-columns: 1fr 1fr;
    }
  }
  .tile {
    text-decoration: none;
    color: inherit;
    display: block;
  }
  .tile:hover .cta {
    color: var(--accent, #3b82f6);
  }
  .muted {
    color: var(--text-muted);
    font-size: 0.9rem;
    line-height: 1.45;
    margin: 0 0 0.75rem;
  }
  .cta {
    font-size: 0.875rem;
    font-weight: 600;
    color: var(--text-muted);
  }
</style>
